// Vite emits the version-matched WASM locally; no CDN or image upload.
async function loadReader() {
  const [reader, { default: wasmUrl }] = await Promise.all([
    import('zxing-wasm/reader'), import('zxing-wasm/reader/zxing_reader.wasm?url'),
  ]);
  await reader.prepareZXingModule({ overrides: { locateFile: path => path.endsWith('.wasm') ? wasmUrl : path }, fireImmediately: true });
  return reader;
}
const cameraError = error => ({
  NotAllowedError: 'El permiso de cámara está bloqueado. Habilítelo en el navegador o use la lectora.',
  NotFoundError: 'No se encontró una cámara. Conecte una o use la lectora.',
  NotReadableError: 'La cámara está ocupada. Cierre otras aplicaciones e intente nuevamente.',
}[error?.name] || 'No se pudo iniciar el lector de cámara. Intente nuevamente o continúe manualmente.');

const READ_OPTIONS = { formats: ['PDF417'], tryHarder: true, maxNumberOfSymbols: 1 };

/** Decodes a still photo of the DNI back locally; resolves the decoded text or ''. */
export async function readDniImage(file, getReader = loadReader) {
  const reader = await getReader();
  const results = await reader.readBarcodes(file, READ_OPTIONS);
  return results.find(result => result.text)?.text || '';
}

/** Owns one session, including permission results arriving after cancellation. */
export function startDniCamera({ video, onRead, onReady, onError, onDevices, deviceId = '',
  mediaDevices = navigator.mediaDevices, secure = window.isSecureContext,
  createCanvas = () => document.createElement('canvas'), loadReader: getReader = loadReader }) {
  let stopped = false, stream, timer, track;
  const stop = () => {
    stopped = true; clearTimeout(timer);
    track?.removeEventListener('ended', ended);
    stream?.getTracks().forEach(item => item.stop()); stream = null;
    video.srcObject = null;
  };
  const fail = error => { if (stopped) return; stop(); onError(cameraError(error)); };
  const ended = () => fail({ name: 'NotReadableError' });
  (async () => {
    try {
      if (!secure || !mediaDevices?.getUserMedia) {
        stopped = true;
        onError('La cámara requiere HTTPS y un navegador compatible. Puede usar la lectora o completar los datos manualmente.');
        return;
      }
      // A chosen device wins: built-in notebook cameras are often fixed-focus and cannot read PDF417 up close.
      const acquired = await mediaDevices.getUserMedia({ audio: false, video: {
        width: { ideal: 1920 }, height: { ideal: 1080 },
        ...(deviceId ? { deviceId: { exact: deviceId } } : { facingMode: { ideal: 'environment' } }),
      } });
      if (stopped) { acquired.getTracks().forEach(item => item.stop()); return; }
      // Labels are only exposed after permission is granted.
      mediaDevices.enumerateDevices?.().then(list => {
        if (!stopped) onDevices?.(list.filter(item => item.kind === 'videoinput').map(item => ({ deviceId: item.deviceId, label: item.label })));
      }).catch(() => {});
      stream = acquired; track = stream.getVideoTracks()[0]; track?.addEventListener('ended', ended);
      // Dense PDF417 codes rarely decode without continuous autofocus.
      if (track?.getCapabilities?.()?.focusMode?.includes?.('continuous')) {
        track.applyConstraints({ advanced: [{ focusMode: 'continuous' }] }).catch(() => {});
      }
      video.srcObject = stream; await video.play();
      const reader = await getReader();
      if (stopped) return;
      onReady?.();
      const canvas = createCanvas(), context = canvas.getContext('2d', { willReadFrequently: true });
      if (!context) throw new Error('Canvas unavailable');
      const read = async () => {
        if (stopped) return;
        try {
          if (video.readyState >= 2 && video.videoWidth && video.videoHeight) {
            canvas.width = video.videoWidth; canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0);
            // A single frame that fails to decode must not end the session.
            const results = await reader.readBarcodes(context.getImageData(0, 0, canvas.width, canvas.height), READ_OPTIONS)
              .catch(() => []);
            if (stopped) return;
            if (results.some(result => result.text && onRead(result.text))) { stop(); return; }
          }
          timer = setTimeout(read, 120);
        } catch (error) { fail(error); }
      };
      await read();
    } catch (error) { fail(error); }
  })();
  return stop;
}
